import Map, { MapRef, Marker, Popup } from "react-map-gl";
import { cn, randomId } from "@/lib/utils";
import "mapbox-gl/dist/mapbox-gl.css";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import calculateBounds from "@/map/calculate-bounds";
import MapLocation from "@/map/location";
import "@/map/style.css"

export const MAPBOX_API_KEY = import.meta.env.VITE_MAPBOX_API_KEY;

interface MapboxMapProps {
  locations?: MapLocation[];
  icon?: any;
  style?: object;
  className?: string;
  options?: any;
  zoom?: number;
  markerSize?: number;
  interactive?: boolean;
}

const MapMarker = (props: {
  location: MapLocation,
  markerSize: number | undefined,
}) => {

  const [showPopup, setShowPopup] = useState(false)

  return (
    <Marker
      longitude={props.location.lng}
      latitude={props.location.lat}
      anchor="bottom"
    >
      <svg
        viewBox="-2 -2 28 28"
        width={props.markerSize}
        height={props.markerSize}
        className="inline-block"
        onMouseEnter={() => setShowPopup(true)}
        onMouseLeave={() => setShowPopup(false)}
      >
        {/* Outer pin shape */}
        <path
          d="M12 1C7.589 1 4 4.589 4 9c0 6 8 16 8 16s8-10 8-16c0-4.411-3.589-8-8-8z"
          fill="white"
          stroke="lightgrey"
          strokeWidth="1"
        />
        {/* Inner colored circle */}
        <circle
          cx="12"
          cy="9"
          r="4"
          className={props.location.fillColor}
        />
      </svg>
      {showPopup && !!props.location.tooltip && (
        <Popup
          longitude={props.location.lng}
          latitude={props.location.lat}
          className="pointer-events-none z-[10000]"
          onClose={() => setShowPopup(false)}
          closeButton={false}
        >
          <div
            onMouseEnter={() => setShowPopup(true)}
            onMouseLeave={() => setShowPopup(false)}
          >
            {
              props.location.tooltip
            }
          </div>
        </Popup>)
      }
    </Marker>
  );
};

export type MapboxMapHandle = {
  flyTo: ({ lat, lng }: { lat: number, lng: number }) => void,
  reset: () => void,
}

const MapboxMapComponent = forwardRef<MapboxMapHandle, MapboxMapProps>((
  {
    zoom = 11,
    markerSize = 48,
    interactive = false,
    ...props
  },
  ref
) => {

  const [mapKey, setMapKey] = React.useState(randomId());
  const mapRef = useRef<MapRef>(null)

  const bounds = calculateBounds(props.locations || [])

  useImperativeHandle(ref, () => {
    return {
      flyTo({ lat, lng }: { lat: number, lng: number }) {
        mapRef.current?.flyTo({ center: [lng, lat], duration: 2000, zoom: 12, padding: 50 });
      },
      reset() {
        if (props.locations) {
          mapRef.current?.fitBounds(calculateBounds(props.locations), { padding: 50, duration: 1000, maxZoom: 12 })
        }
      },
    };
  }, [props.locations]);

  return (
    <div className={cn("overflow-hidden", props.className)}>
      {props.locations && props.locations.length !== 0 && (
        <Map
          ref={mapRef}
          key={mapKey}
          mapboxAccessToken={MAPBOX_API_KEY}
          initialViewState={{
            bounds: bounds,
            fitBoundsOptions: { maxZoom: 12, padding: 50 },
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          attributionControl={false}
          interactive={interactive}
        >
          {props.locations?.map((location: MapLocation, index: number) => (
            <MapMarker key={index} location={location} markerSize={markerSize}/>
          ))}

        </Map>
      )}
    </div>
  );
})

export default MapboxMapComponent;
